import React, { useCallback, useEffect, useState } from 'react';
import { Form } from 'antd';
import { api } from '../../services/api';
import {
    WxbButton,
    WxbDatePicker,
    WxbInput,
    WxbModal,
    WxbSelect,
    WxbTag,
    wxbToast,
} from '../wxb-ui';
import OrgUnitSelectorModal from './OrgUnitSelectorModal';
import { GroupIcon, IdCardIcon, PlusIcon, UserIcon } from './OrgWorkbenchIcons';
import {
    EmployeeRoleOption,
    ensureInternEmployeeRole,
    fetchEmployeeRoleOptions,
    findInternRole,
    toEmployeeRoleSelectOptions,
    upsertEmployeeRoleOption,
} from './employeeRoleHelpers';

interface CreateEmployeeModalProps {
    visible: boolean;
    onCancel: () => void;
    onSuccess: () => void;
    defaultUnitId?: number | null;
    defaultUnitName?: string | null;
}

const CreateEmployeeModal: React.FC<CreateEmployeeModalProps> = ({
    visible,
    onCancel,
    onSuccess,
    defaultUnitId = null,
    defaultUnitName = null,
}) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [roleLoading, setRoleLoading] = useState(false);
    const [roleLoadFailed, setRoleLoadFailed] = useState(false);
    const [creatingInternRole, setCreatingInternRole] = useState(false);
    const [availableRoles, setAvailableRoles] = useState<EmployeeRoleOption[]>([]);
    const [selectedUnitId, setSelectedUnitId] = useState<number | null>(null);
    const [selectedUnitName, setSelectedUnitName] = useState<string | null>(null);
    const [selectorVisible, setSelectorVisible] = useState(false);

    // Set default unit when modal opens
    useEffect(() => {
        if (visible) {
            setSelectedUnitId(defaultUnitId ?? null);
            setSelectedUnitName(defaultUnitName ?? null);
            form.resetFields();
            form.setFieldsValue({
                employmentStatus: 'ACTIVE',
            });
        }
    }, [visible, defaultUnitId, defaultUnitName, form]);

    const loadRoles = useCallback(async () => {
        setRoleLoading(true);
        setRoleLoadFailed(false);
        try {
            setAvailableRoles(await fetchEmployeeRoleOptions());
        } catch (err) {
            console.error('Failed to fetch roles', err);
            setRoleLoadFailed(true);
            wxbToast.error('Failed to fetch roles');
        } finally {
            setRoleLoading(false);
        }
    }, []);

    useEffect(() => {
        if (visible) {
            loadRoles();
        }
    }, [visible, loadRoles]);

    const handleCreateInternRole = async () => {
        setCreatingInternRole(true);
        try {
            const internRole = await ensureInternEmployeeRole();
            setAvailableRoles((roles) => upsertEmployeeRoleOption(roles, internRole));
            form.setFieldsValue({ position: internRole.id });
            wxbToast.success('Intern position is ready');
        } catch (error: any) {
            console.error('Failed to create intern role', error);
            if (error?.response?.status !== 403) {
                wxbToast.error('Failed to create intern position');
            }
        } finally {
            setCreatingInternRole(false);
        }
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const payload = {
                employeeCode: values.employeeCode,
                employeeName: values.employeeName,
                primaryRoleId: values.position || null,
                employmentStatus: values.employmentStatus || 'ACTIVE',
                hireDate: values.hireDate ? values.hireDate.format('YYYY-MM-DD') : null,
                unitId: selectedUnitId,
            };

            await api.post('/employees', payload);
            wxbToast.success('Employee created successfully');
            onSuccess();
        } catch (error: any) {
            console.error('Failed to create employee', error);
            if (error?.response?.status === 409) {
                wxbToast.error('Employee code already exists');
            } else {
                wxbToast.error('Failed to create employee');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleUnitSelect = (unitId: number, unitName: string) => {
        setSelectedUnitId(unitId);
        setSelectedUnitName(unitName);
        setSelectorVisible(false);
    };

    const renderStatusOption = (status: string, tone: string) => (
        <span className="orgwb-status-option">
            <span className={`orgwb-status-dot orgwb-status-dot--${tone}`} />
            <span>{status}</span>
        </span>
    );

    const internRole = findInternRole(availableRoles);
    const roleOptions = toEmployeeRoleSelectOptions(availableRoles);
    const statusOptions = [
        { value: 'ACTIVE', label: renderStatusOption('Active', 'success') },
        { value: 'VACATION', label: renderStatusOption('Vacation', 'warning') },
        { value: 'ON LEAVE', label: renderStatusOption('On Leave', 'amber') },
        { value: 'RESIGNED', label: renderStatusOption('Resigned', 'danger') },
    ];

    return (
        <WxbModal
            title="Add Employee"
            open={visible}
            footer={null}
            onCancel={onCancel}
            centered
            width={600}
            className="orgwb-employee-modal"
            forceRender
        >
            <Form
                form={form}
                layout="vertical"
                requiredMark={false}
                className="orgwb-form orgwb-employee-form"
            >
                <section className="orgwb-form-section">
                    <h4 className="orgwb-form-section-title">Basic Info</h4>
                    <div className="orgwb-form-grid">
                        <Form.Item label="Employee ID" name="employeeCode" rules={[{ required: true, message: 'Required' }]}>
                            <WxbInput prefix={<IdCardIcon />} placeholder="e.g. EMP001" />
                        </Form.Item>
                        <Form.Item label="Name" name="employeeName" rules={[{ required: true, message: 'Required' }]}>
                            <WxbInput prefix={<UserIcon />} placeholder="Full name" />
                        </Form.Item>
                    </div>
                </section>

                <section className="orgwb-form-section">
                    <h4 className="orgwb-form-section-title">Professional</h4>
                    <div className="orgwb-form-grid">
                        <div className="orgwb-role-field">
                            <Form.Item label="Position" name="position" rules={[{ required: true, message: 'Select a position' }]}>
                                <WxbSelect
                                    placeholder={roleLoading ? 'Loading roles...' : 'Select Role'}
                                    showSearch
                                    optionFilterProp="label"
                                    loading={roleLoading}
                                    options={roleOptions}
                                />
                            </Form.Item>
                            {roleLoadFailed ? (
                                <div className="orgwb-role-assist orgwb-role-assist--warning" role="status">
                                    <span>Position roles failed to load</span>
                                    <WxbButton type="button" variant="secondary" size="sm" onClick={loadRoles}>
                                        Retry
                                    </WxbButton>
                                </div>
                            ) : !internRole && !roleLoading ? (
                                <div className="orgwb-role-assist" role="status">
                                    <span>Intern position missing</span>
                                    <WxbButton
                                        type="button"
                                        variant="secondary"
                                        size="sm"
                                        disabled={creatingInternRole}
                                        onClick={handleCreateInternRole}
                                    >
                                        <PlusIcon />
                                        {creatingInternRole ? 'Creating...' : 'Create Intern'}
                                    </WxbButton>
                                </div>
                            ) : null}
                        </div>
                    </div>

                    <Form.Item label="Organization">
                        <WxbButton
                            type="button"
                            variant="secondary"
                            className="orgwb-unit-picker"
                            onClick={() => setSelectorVisible(true)}
                        >
                            {selectedUnitName ? (
                                <WxbTag color="blue" icon={<GroupIcon />}>{selectedUnitName}</WxbTag>
                            ) : (
                                <>
                                    <PlusIcon />
                                    Select Unit
                                </>
                            )}
                        </WxbButton>
                    </Form.Item>
                </section>

                <section className="orgwb-form-section">
                    <h4 className="orgwb-form-section-title">Personal</h4>
                    <div className="orgwb-form-grid">
                        <Form.Item label="Status" name="employmentStatus">
                            <WxbSelect options={statusOptions} />
                        </Form.Item>
                        <Form.Item label="Hire Date" name="hireDate">
                            <WxbDatePicker />
                        </Form.Item>
                    </div>
                </section>

                <div className="orgwb-form-actions">
                    <WxbButton type="button" onClick={onCancel} variant="ghost">
                        Cancel
                    </WxbButton>
                    <WxbButton
                        type="button"
                        onClick={handleSave}
                        disabled={loading}
                        variant="primary"
                    >
                        {loading ? 'Creating...' : 'Create Employee'}
                    </WxbButton>
                </div>
            </Form>

            <OrgUnitSelectorModal
                visible={selectorVisible}
                onCancel={() => setSelectorVisible(false)}
                onSelect={handleUnitSelect}
                title="Select Organization Unit"
            />
        </WxbModal>
    );
};

export default CreateEmployeeModal;
